import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { ChevronRight } from 'lucide-react';

export function Skeleton({ height = 64 }: { height?: number }) {
  return (
    <div
      className="skeleton"
      style={{ height }}
      role="status"
      aria-label="Loading"
      aria-busy="true"
    />
  );
}

export function MetricCardSkeletonGrid({ count = 5 }: { count?: number }) {
  return (
    <div className="kpi-strip">
      {Array.from({ length: count }).map((_, i) => (
        <Skeleton key={i} height={140} />
      ))}
    </div>
  );
}

export function StatusCardSkeletonGrid({ count = 5 }: { count?: number }) {
  return (
    <div className="flow-grid">
      {Array.from({ length: count }).map((_, i) => (
        <Skeleton key={i} height={148} />
      ))}
    </div>
  );
}

/**
 * Shared large dashboard metric card.
 * Used by Business Snapshot, Order Flow and Needs Attention.
 */
export function DashboardMetricCard({
  icon,
  label,
  value,
  description,
  to,
  search,
  onOpen,
  loading,
  tone = 'neutral',
  className = '',
  step,
  showChevron = true,
  children,
}: {
  icon: ReactNode;
  label: string;
  value?: number | string | null;
  description?: string;
  to?: string;
  search?: Record<string, unknown>;
  onOpen?: () => void;
  loading?: boolean;
  tone?: string;
  className?: string;
  step?: number;
  showChevron?: boolean;
  children?: ReactNode;
}) {
  const body = (
    <>
      <div className="dash-card-top">
        <span className={`dash-card-icon tone-${tone}`} aria-hidden>
          {icon}
        </span>
        <div className="dash-card-heading">
          {step != null ? <span className="dash-card-step">Step {step}</span> : null}
          <span className="dash-card-label">{label}</span>
        </div>
        {showChevron && (to || onOpen) ? (
          <ChevronRight className="dash-card-chevron" size={18} aria-hidden />
        ) : null}
      </div>
      <div className="dash-card-value">{loading ? <Skeleton height={34} /> : (value ?? '—')}</div>
      {description ? <div className="dash-card-desc">{description}</div> : null}
      {children}
    </>
  );

  const classes = `dash-card ${className} ${to || onOpen ? 'dash-card-link' : ''}`;

  if (to && !loading) {
    return (
      <Link
        to={to as never}
        search={search as never}
        className={classes}
        aria-label={`${label}: ${value ?? 0}. Open ${label}`}
      >
        {body}
      </Link>
    );
  }

  if (onOpen && !loading) {
    return (
      <button
        type="button"
        className={`${classes} dash-card-button`}
        onClick={onOpen}
        aria-label={`Open ${label} details`}
      >
        {body}
      </button>
    );
  }

  return <div className={classes}>{body}</div>;
}

/** Compact clickable KPI used in the Business Snapshot strip. */
export function MetricCard({
  icon,
  label,
  value,
  subtitle,
  to,
  search,
  loading,
  onOpen,
  tone = 'neutral',
  wide = false,
}: {
  icon: ReactNode;
  label: string;
  value?: number | string | null;
  subtitle?: string;
  to?: string;
  search?: Record<string, unknown>;
  loading?: boolean;
  onOpen?: () => void;
  tone?: 'neutral' | 'green' | 'teal' | 'amber';
  wide?: boolean;
}) {
  return (
    <DashboardMetricCard
      icon={icon}
      label={label}
      value={value}
      description={subtitle}
      to={to}
      search={search}
      onOpen={onOpen}
      loading={loading}
      tone={tone}
      className={`kpi-card ${wide ? 'kpi-card-wide' : ''}`}
    />
  );
}

/** One step in the order lifecycle flow. */
export function OrderFlowStepCard({
  icon,
  label,
  value,
  description,
  tone,
  to,
  search,
  loading,
  step,
}: {
  icon: ReactNode;
  label: string;
  value?: number | string | null;
  description?: string;
  tone: 'neutral' | 'progress' | 'success';
  to: string;
  search?: Record<string, unknown>;
  loading?: boolean;
  step?: number;
}) {
  return (
    <DashboardMetricCard
      icon={icon}
      label={label}
      value={value}
      description={description}
      to={to}
      search={search}
      loading={loading}
      tone={tone}
      step={step}
      className={`flow-step tone-${tone}`}
    />
  );
}

export function FlowArrow() {
  return (
    <div className="flow-arrow" aria-hidden>
      <svg width="22" height="22" viewBox="0 0 20 20" fill="none">
        <path
          d="M4 10h10m0 0-3.5-3.5M14 10l-3.5 3.5"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
}

/** Exception / attention metric card. */
export function AttentionCard({
  icon,
  label,
  value,
  description,
  tone,
  to,
  search,
  loading,
}: {
  icon: ReactNode;
  label: string;
  value?: number | string | null;
  description?: string;
  tone: 'warning' | 'error' | 'refund' | 'neutral';
  to: string;
  search?: Record<string, unknown>;
  loading?: boolean;
}) {
  const n = Number(value);
  const hasSignal = Number.isFinite(n) && n > 0;
  return (
    <DashboardMetricCard
      icon={icon}
      label={label}
      value={value}
      description={description}
      to={to}
      search={search}
      loading={loading}
      tone={tone}
      className={`attention-card tone-${tone} ${hasSignal ? 'has-signal' : ''}`}
    />
  );
}

export function PeriodTabs({
  value,
  onChange,
  options,
  name,
}: {
  value: string;
  onChange: (v: string) => void;
  options: Array<{ id: string; label: string }>;
  name: string;
}) {
  return (
    <div className="dash-period-tabs" role="tablist" aria-label={name}>
      {options.map((opt) => (
        <button
          key={opt.id}
          type="button"
          role="tab"
          aria-selected={value === opt.id}
          className={`period-tab ${value === opt.id ? 'active' : ''}`}
          onClick={() => onChange(opt.id)}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

export function SectionHeader({
  eyebrow,
  title,
  description,
  aside,
}: {
  eyebrow?: string;
  title: string;
  description?: string;
  aside?: ReactNode;
}) {
  return (
    <header className="section-header">
      <div className="section-header-text">
        {eyebrow ? <p className="section-eyebrow">{eyebrow}</p> : null}
        <h2 className="section-title">{title}</h2>
        {description ? <p className="section-desc">{description}</p> : null}
      </div>
      {aside ? <div className="section-aside">{aside}</div> : null}
    </header>
  );
}

export function ChartFrame({
  title,
  aside,
  children,
  description,
}: {
  title: string;
  aside?: ReactNode;
  children: ReactNode;
  description?: string;
}) {
  return (
    <section className="chart-frame card" aria-label={title}>
      <header className="chart-frame-header">
        <div>
          <h2 className="chart-title">{title}</h2>
          {description ? <p className="muted chart-desc">{description}</p> : null}
        </div>
        {aside}
      </header>
      {children}
    </section>
  );
}

export function EmptyChartState({
  title = 'No data yet',
  description,
}: {
  title?: string;
  description?: string;
}) {
  return (
    <div className="empty-chart-state" role="status">
      <svg width="36" height="36" viewBox="0 0 36 36" fill="none" aria-hidden>
        <rect x="4" y="20" width="6" height="12" rx="1.5" fill="#c5d9cc" />
        <rect x="15" y="12" width="6" height="20" rx="1.5" fill="#c5d9cc" />
        <rect x="26" y="6" width="6" height="26" rx="1.5" fill="#d7e6dd" />
      </svg>
      <p className="empty-chart-title">{title}</p>
      {description ? <p className="empty-chart-desc">{description}</p> : null}
    </div>
  );
}
